import { useEffect, useState, useCallback, useRef } from "react";
import { Camera, Trash2, Loader2, Search, ChevronLeft, ChevronRight, X, Pencil, Play, Film, Image as ImageIcon, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { compressVideo, MAX_VIDEO_BYTES } from "@/lib/videoCompress";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRODUCT_GROUPS } from "@/lib/productGroups";

const NONE = "__none__";

export const GalleryRealizations = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = user.role === "admin";
  const [photos, setPhotos] = useState([]);
  const [methods, setMethods] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const shortClient = (name) => clientMap[(name || "").trim().toLowerCase()] || name || "";
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [videoStatus, setVideoStatus] = useState(null);
  const [coverQueue, setCoverQueue] = useState([]);
  const [coverBusy, setCoverBusy] = useState(false);
  const coverVideoRef = useRef(null);
  const coverTarget = coverQueue[0] || null;
  const [dragActive, setDragActive] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [sortBy, setSortBy] = useState("added");
  const [viewIdx, setViewIdx] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const pasteBoxRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("empro_token") : "";
  const photoUrl = (p) => `${API}/photos/${p.id}?token=${token}`;
  const coverUrl = (p) => `${API}/gallery/videos/${p.id}/cover?token=${token}&v=${p.poster_gridfs_id || ""}`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery/photos");
      setPhotos(data);
      setDrafts(Object.fromEntries(data.map((p) => [p.id, { client: p.client || "", branding: p.branding || "", taken_on: p.taken_on || "", public: !!p.public, type_label: p.type_label || "" }])));
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/catalog").then((r) => setMethods(r.data?.branding_types || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/customers/names").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/customers/short-names").then((r) => setClientMap(Object.fromEntries((r.data || []).map((c) => [(c.name || "").trim().toLowerCase(), (c.short || "").trim() || c.name])))).catch(() => {}); }, []);

  const filtered = photos.filter((p) =>
    (!search.trim() || (p.client || "").toLowerCase().includes(search.trim().toLowerCase())) &&
    (methodFilter === "all" || (p.branding || "") === methodFilter));
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "added") return 0;
    const av = a.taken_on || "", bv = b.taken_on || "";
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    return sortBy === "date_desc" ? bv.localeCompare(av) : av.localeCompare(bv);
  });

  const isVideo = (f) => f.type.startsWith("video/") || /\.(mov|mp4|webm|m4v|mkv|ogg)$/i.test(f.name);

  const uploadVideo = async (file) => {
    setVideoStatus({ name: file.name, phase: "compress", pct: 0 });
    const { blob, mime } = await compressVideo(file, (p) => setVideoStatus((s) => (s ? { ...s, pct: Math.round(p * 100) } : s)));
    setVideoStatus((s) => (s ? { ...s, phase: "upload", pct: 0 } : s));
    const { data: init } = await api.post("/gallery/videos/init");
    const uid = init.upload_id;
    const CHUNK = 4 * 1024 * 1024;
    let off = 0;
    while (off < blob.size) {
      const slice = blob.slice(off, off + CHUNK);
      const fd = new FormData(); fd.append("upload_id", uid); fd.append("chunk", slice, "chunk");
      await api.post("/gallery/videos/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      off += CHUNK;
      setVideoStatus((s) => (s ? { ...s, pct: Math.round(Math.min(100, (off / blob.size) * 100)) } : s));
    }
    const fd2 = new FormData(); fd2.append("upload_id", uid); fd2.append("content_type", mime);
    const { data: done } = await api.post("/gallery/videos/complete", fd2, { headers: { "Content-Type": "multipart/form-data" } });
    const url = URL.createObjectURL(blob);
    return { id: done.id, src: url, blobUrl: url };
  };

  const closeCover = () => {
    coverQueue.forEach((c) => { if (c.blobUrl) { try { URL.revokeObjectURL(c.blobUrl); } catch { /* noop */ } } });
    setCoverQueue([]);
  };
  const nextCover = () => {
    setCoverQueue((q) => {
      const cur = q[0];
      if (cur?.blobUrl) { try { URL.revokeObjectURL(cur.blobUrl); } catch { /* noop */ } }
      return q.slice(1);
    });
  };
  const captureCover = async () => {
    const v = coverVideoRef.current;
    if (!v || !v.videoWidth) return toast.error("Poczekaj, aż film się załaduje");
    setCoverBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth; canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("Nie udało się pobrać klatki (spróbuj inną chwilę filmu)");
      const fd = new FormData(); fd.append("file", blob, "cover.jpg");
      await api.post(`/gallery/photos/${coverTarget.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Okładka ustawiona");
      nextCover();
      load();
    } catch (e) { toast.error(e?.message || errMsg(e)); }
    finally { setCoverBusy(false); }
  };

  const uploadFiles = async (fileList) => {
    const all = Array.from(fileList || []);
    const images = all.filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    const videos = all.filter((f) => isVideo(f));
    if (!images.length && !videos.length) return toast.error("Upuść pliki graficzne lub wideo (JPG, PNG, HEIC, MP4, MOV)");
    setUploading(images.length);
    let ok = 0;
    for (const f of images) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post("/gallery/photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
      setUploading((n) => n - 1);
    }
    setUploading(0);
    let okV = 0; const newCovers = [];
    for (const f of videos) {
      try { const r = await uploadVideo(f); okV++; if (r?.id) newCovers.push({ id: r.id, src: r.src, blobUrl: r.blobUrl }); }
      catch (err) { toast.error(`${f.name}: ${err?.message || errMsg(err)}`); }
    }
    setVideoStatus(null);
    const total = ok + okV;
    if (total) { toast.success(`Dodano plików: ${total}`); toast.info("Kliknij „Edytuj” pod miniaturą, aby dodać klienta i sposób znakowania.", { duration: 6000 }); }
    if (newCovers.length) { setCoverQueue(newCovers); toast.info("Wybierz okładkę (miniaturę) dla dodanych filmów.", { duration: 6000 }); }
    load();
  };
  const onUpload = (e) => { const files = e.target.files; e.target.value = ""; uploadFiles(files); };
  const onDrop = (e) => { e.preventDefault(); setDragActive(false); uploadFiles(e.dataTransfer.files); };

  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  const pasteOpenRef = useRef(pasteOpen);
  pasteOpenRef.current = pasteOpen;
  useEffect(() => {
    const onPaste = (e) => {
      if (pasteOpenRef.current || e.defaultPrevented) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) files.push(f); }
      }
      if (files.length) { e.preventDefault(); uploadFilesRef.current(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const pasteFromClipboard = async () => {
    if (navigator.clipboard?.read) {
      try {
        const items = await navigator.clipboard.read();
        const files = [];
        for (const it of items) {
          const type = it.types.find((t) => t.startsWith("image/"));
          if (type) { const blob = await it.getType(type); files.push(new File([blob], `schowek-${Date.now()}.${(type.split("/")[1] || "png")}`, { type })); }
        }
        if (files.length) { uploadFiles(files); return; }
      } catch { /* przeglądarka blokuje odczyt (np. Firefox) — pokaż okno ręcznego wklejenia */ }
    }
    setPasteOpen(true);
    setTimeout(() => pasteBoxRef.current?.focus(), 150);
  };

  const onPasteBox = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const items = e.clipboardData?.items;
    const files = [];
    if (items) for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) files.push(f); }
    }
    if (files.length) { setPasteOpen(false); uploadFiles(files); }
    else { toast.error("W schowku nie ma obrazu — skopiuj najpierw zdjęcie (np. Print Screen)"); }
  };

  const setDraft = (pid, field, val) => setDrafts((d) => ({ ...d, [pid]: { ...(d[pid] || {}), [field]: val } }));
  const persist = async (pid, next) => {
    try {
      await api.put(`/gallery/photos/${pid}`, { client: next.client || "", branding: next.branding || "", taken_on: next.taken_on || "", public: !!next.public, type_label: next.type_label || "" });
      setPhotos((ps) => ps.map((p) => (p.id === pid ? { ...p, client: next.client || "", branding: next.branding || "", taken_on: next.taken_on || "", public: !!next.public, type_label: next.type_label || "" } : p)));
      setDrafts((d) => ({ ...d, [pid]: { ...next } }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (pid) => {
    if (!(await confirm({ title: "Usunąć zdjęcie?", description: "Czy na pewno usunąć to zdjęcie z galerii realizacji?", confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/gallery/photos/${pid}`); setPhotos((ps) => ps.filter((p) => p.id !== pid)); toast.success("Zdjęcie usunięte"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const step = useCallback((dir) => {
    setViewIdx((i) => {
      if (i === null) return i;
      const n = i + dir;
      if (n < 0 || n >= sorted.length) return i;
      return n;
    });
  }, [sorted.length]);

  useEffect(() => {
    if (viewIdx === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "Escape") setViewIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewIdx, step]);

  const current = viewIdx !== null ? sorted[viewIdx] : null;

  return (
    <div data-testid="gallery-realizations" className="space-y-5">
      <datalist id="gallery-clients-list">
        {clients.map((c) => <option key={c} value={c} />)}
      </datalist>
      <div>
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Galeria realizacji</h2>
        <p className="text-sm text-muted-foreground mt-1">Wspólna galeria dla wszystkich użytkowników. Zdjęcia są automatycznie zmniejszane (maks. 1600 px, JPEG), obsługiwany też format HEIC z iPhone'a. Na każde dodane zdjęcie automatycznie nanoszony jest znak wodny „EMPRO WORKWEAR". Możesz też wgrywać filmy (MP4/MOV) — są automatycznie kompresowane w przeglądarce do maks. 50 MB.</p>
      </div>

      <label data-testid="gallery-upload-label"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 cursor-pointer rounded-xl border-2 border-dashed py-8 transition-colors ${dragActive ? "border-sky-400 bg-sky-400/10" : "border-border hover:border-sky-400/60 hover:bg-sky-400/5"}`}>
        {uploading > 0 || videoStatus ? <Loader2 className="h-8 w-8 text-sky-400 animate-spin" /> : <Camera className="h-8 w-8 text-sky-400" />}
        <span className="text-sm font-semibold" data-testid="gallery-upload-status">
          {videoStatus ? `${videoStatus.phase === "compress" ? "Kompresja filmu" : "Wysyłanie filmu"}… ${videoStatus.pct}%`
            : uploading > 0 ? `Wgrywanie… (pozostało ${uploading})`
            : dragActive ? "Upuść pliki tutaj" : "Zrób zdjęcie, wybierz lub przeciągnij pliki"}
        </span>
        <span className="text-[11px] text-muted-foreground">Przeciągnij i upuść, kliknij lub wklej ze schowka (Ctrl+V) • kilka naraz • JPG, PNG, HEIC, MP4, MOV</span>
        {videoStatus && <span className="text-[10px] text-amber-400">Kompresja trwa mniej więcej tyle, co długość filmu — nie zamykaj karty.</span>}
        <input data-testid="gallery-upload-input" type="file" accept="image/*,.heic,.heif,video/*,.mov,.mp4,.webm,.m4v" capture="environment" multiple className="hidden" onChange={onUpload} disabled={uploading > 0 || !!videoStatus} />
      </label>

      <div className="flex justify-center">
        <Button data-testid="gallery-paste-btn" type="button" variant="outline" size="sm" onClick={pasteFromClipboard} disabled={uploading > 0 || !!videoStatus} className="gap-1.5 border-sky-400/40 text-sky-400 hover:bg-sky-400/10">
          <ClipboardPaste className="h-4 w-4" /> Wklej obraz ze schowka
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="gallery-search" list="gallery-clients-list" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj po kliencie…" className="pl-9" />
        </div>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger data-testid="gallery-method-filter" className="sm:w-[240px]"><SelectValue placeholder="Metoda znakowania" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie metody znakowania</SelectItem>
            {methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="gallery-sort" className="sm:w-[220px]"><SelectValue placeholder="Sortuj" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="added">Kolejność dodania</SelectItem>
            <SelectItem value="date_desc">Data zdjęcia: najnowsze</SelectItem>
            <SelectItem value="date_asc">Data zdjęcia: najstarsze</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="gallery-empty" className="text-center text-sm text-muted-foreground py-8">{photos.length === 0 ? "Brak zdjęć w galerii. Dodaj pierwsze realizacje powyżej." : "Brak zdjęć dla wybranych filtrów."}</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5" data-testid="gallery-grid">
          {sorted.map((p, i) => (
              <div key={p.id} data-testid={`gallery-item-${p.id}`} className="rounded-lg border border-border bg-secondary/40 overflow-hidden flex flex-col">
                <div className="relative group aspect-square bg-secondary">
                  {p.media_type === "video" ? (
                    <div className="h-full w-full cursor-zoom-in" onClick={() => setViewIdx(i)}>
                      {p.poster_gridfs_id ? (
                        <img src={coverUrl(p)} alt={p.client || "Realizacja (wideo)"} loading="lazy" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-secondary"><Film className="h-7 w-7 text-muted-foreground" /></div>
                      )}
                      <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="rounded-full bg-black/60 p-2"><Play className="h-5 w-5 text-white" fill="white" /></span></span>
                      <span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-bold text-white"><Film className="h-2.5 w-2.5" />WIDEO</span>
                      {!p.poster_gridfs_id && <span className="absolute top-1 left-1 rounded bg-rose-500 px-1 py-0.5 text-[8px] font-bold uppercase text-white">Brak okładki</span>}
                    </div>
                  ) : (
                    <img src={photoUrl(p)} alt={p.client || "Realizacja"} loading="lazy" className="h-full w-full object-cover cursor-zoom-in" onClick={() => setViewIdx(i)} />
                  )}
                  {isAdmin && <button data-testid={`gallery-delete-${p.id}`} onClick={() => del(p.id)} className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-500" title="Usuń"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <div className="p-1.5 flex flex-col gap-1">
                  <div data-testid={`gallery-caption-${p.id}`} className="min-h-[1rem] leading-tight truncate">
                    {p.public && <span className="mr-1 rounded bg-emerald-500/20 px-1 text-[9px] font-bold uppercase text-emerald-400" title="Widoczne na stronie publicznej">PUB</span>}
                    {(p.branding || p.client) ? (
                      <span className="text-[11px] truncate">
                        {p.branding && <span className="font-bold text-amber-400">{p.branding}</span>}
                        {p.branding && p.client && <span className="text-muted-foreground"> · </span>}
                        {p.client && <span className="font-semibold">{shortClient(p.client)}</span>}
                      </span>
                    ) : <span className="text-[10px] italic text-muted-foreground">Bez opisu</span>}
                  </div>
                  <button data-testid={`gallery-edit-${p.id}`} onClick={() => setEditingId(editingId === p.id ? null : p.id)} className="flex items-center gap-1 self-start text-[10px] font-semibold text-sky-400 hover:text-sky-300"><Pencil className="h-3 w-3" /> {editingId === p.id ? "Gotowe" : "Edytuj"}</button>
                  {editingId === p.id && (
                    <div className="space-y-1.5 pt-0.5">
                      {p.media_type === "video" && (
                        <button data-testid={`gallery-cover-${p.id}`} onClick={() => setCoverQueue([{ id: p.id, src: photoUrl(p), remote: true }])} className="flex w-full items-center justify-center gap-1 rounded-md border border-amber-500/40 py-1 text-[10px] font-bold text-amber-400 hover:bg-amber-500/10"><ImageIcon className="h-3 w-3" /> {p.poster_gridfs_id ? "Zmień okładkę" : "Wybierz okładkę"}</button>
                      )}
                      <Input data-testid={`gallery-client-${p.id}`} list="gallery-clients-list" value={(drafts[p.id]?.client) || ""} onChange={(e) => setDraft(p.id, "client", e.target.value)} onBlur={() => persist(p.id, drafts[p.id] || {})} placeholder="Nazwa klienta (z listy lub wpisz)" className="h-8 text-xs" />
                      <Select value={(drafts[p.id]?.branding) || NONE} onValueChange={(v) => { const next = { ...(drafts[p.id] || {}), branding: v === NONE ? "" : v }; persist(p.id, next); }}>
                        <SelectTrigger data-testid={`gallery-branding-${p.id}`} className="h-8 text-xs"><SelectValue placeholder="Sposób znakowania" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— brak —</SelectItem>
                          {methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input data-testid={`gallery-date-${p.id}`} type="date" value={(drafts[p.id]?.taken_on) || ""} onChange={(e) => { const next = { ...(drafts[p.id] || {}), taken_on: e.target.value }; persist(p.id, next); }} className="h-8 text-xs" title="Data wykonania" />
                      <Select value={(drafts[p.id]?.type_label) || NONE} onValueChange={(v) => { const next = { ...(drafts[p.id] || {}), type_label: v === NONE ? "" : v }; persist(p.id, next); }}>
                        <SelectTrigger data-testid={`gallery-type-${p.id}`} className="h-8 text-xs"><SelectValue placeholder="Grupa produktu (do publikacji)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— brak grupy —</SelectItem>
                          {(drafts[p.id]?.type_label) && !PRODUCT_GROUPS.includes(drafts[p.id].type_label) && (
                            <SelectItem value={drafts[p.id].type_label}>{drafts[p.id].type_label} (stara wartość)</SelectItem>
                          )}
                          {PRODUCT_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <label data-testid={`gallery-public-${p.id}`} className="flex items-center gap-1.5 text-[10px] font-semibold cursor-pointer select-none rounded-md border border-emerald-500/40 px-2 py-1.5 hover:bg-emerald-500/10">
                        <input type="checkbox" checked={!!(drafts[p.id]?.public)} onChange={(e) => { const next = { ...(drafts[p.id] || {}), public: e.target.checked }; persist(p.id, next); }} className="accent-emerald-500" />
                        Opublikuj publicznie (Nasze realizacje)
                      </label>
                      {p.uploaded_by_name && <p className="text-[10px] text-muted-foreground truncate">Dodał: {p.uploaded_by_name}</p>}
                      <button data-testid={`gallery-edit-delete-${p.id}`} onClick={() => del(p.id)} className="mt-0.5 flex w-full items-center justify-center gap-1 rounded-md border border-rose-500/40 py-1 text-[10px] font-bold text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3 w-3" /> Usuń zdjęcie</button>
                    </div>
                  )}
                </div>
              </div>
          ))}
        </div>
      )}

      {current && (
        <div data-testid="gallery-viewer" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4" onClick={() => setViewIdx(null)}>
          <button data-testid="gallery-viewer-close" onClick={(e) => { e.stopPropagation(); setViewIdx(null); }} className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
          <button data-testid="gallery-viewer-prev" disabled={viewIdx === 0} onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-3 sm:left-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="h-6 w-6" /></button>
          <button data-testid="gallery-viewer-next" disabled={viewIdx === sorted.length - 1} onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-3 sm:right-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="h-6 w-6" /></button>
          <div className="flex flex-col items-center gap-3 max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {current.media_type === "video" ? (
              <video src={photoUrl(current)} controls autoPlay playsInline className="max-h-[78vh] w-auto object-contain rounded bg-black" />
            ) : (
              <img src={photoUrl(current)} alt={current.client || "Realizacja"} className="max-h-[78vh] w-auto object-contain rounded" />
            )}
            <div data-testid="gallery-viewer-meta" className="text-center text-white/90 text-sm">
              <span className="font-semibold">{shortClient(current.client) || "Bez klienta"}</span>
              {current.branding && <span className="ml-2 text-amber-400">• {current.branding}</span>}
              {current.taken_on && <span className="ml-2 text-white/60">• {current.taken_on}</span>}
              <span className="ml-3 text-white/40 font-mono text-xs">{viewIdx + 1} / {sorted.length}</span>
            </div>
          </div>
        </div>
      )}

      <Dialog open={coverQueue.length > 0} onOpenChange={(v) => !v && closeCover()}>
        <DialogContent data-testid="cover-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Wybierz okładkę filmu</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Przewiń film do wybranej klatki, a następnie kliknij „Ustaw jako okładkę”. To zdjęcie będzie miniaturą filmu w galerii.{coverQueue.length > 1 ? ` Pozostało filmów: ${coverQueue.length}` : ""}</DialogDescription>
          </DialogHeader>
          {coverTarget && (
            <div className="space-y-3">
              <video key={coverTarget.id} ref={coverVideoRef} src={coverTarget.src} crossOrigin="anonymous" controls muted playsInline className="w-full max-h-[55vh] rounded bg-black" />
              <Button data-testid="cover-capture" onClick={captureCover} disabled={coverBusy} className="w-full gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{coverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}Ustaw bieżącą klatkę jako okładkę</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent data-testid="paste-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Wklej obraz ze schowka</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Kliknij w poniższe pole i naciśnij <b>Ctrl+V</b> (na Macu <b>Cmd+V</b>), aby wkleić skopiowany obraz. To rozwiązanie działa we wszystkich przeglądarkach, w tym Firefox.</DialogDescription>
          </DialogHeader>
          <div
            ref={pasteBoxRef}
            data-testid="paste-dropzone"
            contentEditable
            suppressContentEditableWarning
            onPaste={onPasteBox}
            onBeforeInput={(e) => e.preventDefault()}
            onKeyDown={(e) => { if (!(e.ctrlKey || e.metaKey)) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const imgs = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/")); if (imgs.length) { setPasteOpen(false); uploadFiles(imgs); } }}
            tabIndex={0}
            className="min-h-[120px] rounded-xl border-2 border-dashed border-sky-400/50 bg-sky-400/5 flex items-center justify-center text-center text-sm font-semibold text-sky-400 outline-none focus:border-sky-400 focus:bg-sky-400/10 cursor-text select-none px-4"
          >
            Kliknij tutaj i naciśnij Ctrl+V
          </div>
          <p className="text-[11px] text-muted-foreground">Wskazówka: najpierw zrób zrzut ekranu (Print Screen) lub skopiuj obraz prawym przyciskiem → „Kopiuj obraz”.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};
